import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { ChangeEvent, useContext, useState } from "react";
import {
  Link as RouterLink,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";

import { PageNavigation } from "../../../components/PageNavigation/PageNavigation";
import { AppContext } from "../../../core/context/appContextProvider";
import RoutingPaths from "../../../core/routing/routingPaths";
import useRole from "../../../core/routing/useRole";
import { getFund } from "../../../services/fund.service";
import { useClientEffect } from "../../../services/hooks/useClientsEffect/useClientEffect.hooks";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import useWindowSize from "../../../utils/hooks/useWindowSize.hook";
import { ScopeRole } from "../../../utils/types/user.type";
import { AllocationFiles } from "../allocationFiles/AllocationFiles";
import { AllocationWorkbook } from "../allocationWorkbook/AllocationWorkbook";
import { FundDetails } from "../fundDetails/FundDetails";
import { ReadOnlyFundDetails } from "../fundDetails/ReadOnlyFundDetails";
import { FundWrapper } from "../fundList/FundList.styles";
import { InvestmentTransactionList } from "../InvestmentTransactions/InvestmentTransactionList";
import { JournalEntries } from "../JournalEntries/JournalEntries";
import { Partners } from "../partners/Partners";
import { Performance } from "../performance/Performance";
import { PortfolioCompanyList } from "../portfolioCompanies/PortfolioCompanyList";
import { QuartersManagement } from "../quartersManagement/QuartersManagement";
import { ScheduleOfInvestments } from "../scheduleOfInvestments/ScheduleOfInvestments";
import TransactionMapper from "../transactionMapper/TransactionMapper";
import { usepageMenuOptions } from "./pageMenuOptions.hooks";

export const FundNavigation: React.FC = () => {
  const location = useLocation();
  const [fundName, setFundName] = useState<string>();
  const history = useHistory();
  const { hasRole: isFundAdmin } = useRole([ScopeRole.FUND_USER_ADMIN]);
  const params = useParams();
  const { menu } = usepageMenuOptions();
  const isAllocationFile = location.pathname.startsWith(
    "/fund-nav/fund-allocations-list-file"
  );
  let fundId: string;

  if (isAllocationFile) {
    // @ts-ignore: expression error
    fundId = params[0] as string;
    fundId = fundId.split("/")[1];
  } else {
    // @ts-ignore: expression error
    fundId = params["fundId"] as string;
  }
  const { state, informationAlert } = useContext(AppContext);

  const clientId = state.loginUser.clientId;
  const { client } = useClientEffect(clientId !== "new" ? clientId : undefined);
  const isNewFundDetail =
    fundId === "new" && location.pathname.startsWith("/fund-nav/fund-details");
  const showPageNav = !(isNewFundDetail || isAllocationFile);

  const backArrowLocation = isAllocationFile
    ? `/fund-nav/fund-allocations-list/${fundId}`
    : "/funds";
  const backArrowName = isAllocationFile ? "Allocation Files" : fundName;

  useEffectAsync(async (isCanceled) => {
    if (fundId === "new") {
      setFundName("New Fund");
    } else {
      const fund = await getFund(fundId);

      if (isCanceled()) return;

      setFundName(fund.fundDetail.name);
    }
  }, []);

  const window = useWindowSize();
  const wrapperHeight = window.height - 80;

  return (
    <FundWrapper wrapperHeight={wrapperHeight}>
      <Grid
        style={{ margin: "10px" }}
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Button
          onClick={() => {
            history.push(backArrowLocation);
          }}
          startIcon={<ArrowBack />}
        >
          <Typography>{backArrowName}</Typography>
        </Button>
      </Grid>
      <Divider />
      {showPageNav && <PageNavigation fundId={fundId} menu={menu} />}
      <Switch>
        <Route
          exact
          path={`${RoutingPaths.FundDetails}/:fundId`}
          component={isFundAdmin ? ReadOnlyFundDetails : FundDetails}
        />
        <Route
          exact
          path={`${RoutingPaths.TransactionMapper}/:fundId`}
          component={TransactionMapper}
        />
        <Route
          exact
          path={`${RoutingPaths.FundAllocationsList}/:fundId`}
          component={AllocationFiles}
        />
        <Route
          exact
          path={`${RoutingPaths.FundAllocationsFile}/:fundId/:workbookId`}
          component={AllocationWorkbook}
        />
        <Route
          exact
          path={`${RoutingPaths.FundPortfolioCompanies}/:fundId`}
          component={PortfolioCompanyList}
        />
        <Route
          exact
          path={`${RoutingPaths.ScheduleOfInvestmentsFund}/:fundId`}
          component={ScheduleOfInvestments}
        />
        <Route
          exact
          path={`${RoutingPaths.InvestmentTransactionsFund}/:fundId`}
          component={InvestmentTransactionList}
        />
        <Route exact path={`${RoutingPaths.JournalEntries}/:fundId`} component={JournalEntries} />
        <Route>No Fund Nav Route! A route need to be wired up.</Route>
      </Switch>
    </FundWrapper>
  );
};
